/**
 * CharactersPanel — character management with side-by-side merge UI.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { useLightbox } from "@/components/ImageLightbox";
import { analyzeScriptCharacters } from "@/lib/screenplayDetector";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X, ChevronDown, ChevronRight, GitMerge, AlertCircle, Check, GripVertical, Pin, MapPin, Loader2, BookOpenCheck } from "lucide-react";

const SHALLOW_FIELDS = [
  { key: "gender", label: "Gender" },
  { key: "age", label: "Age" },
  { key: "description", label: "Description", multiline: true },
];

const DEEP_FIELDS = [
  { key: "role", label: "Story role" },
  { key: "occupation", label: "Occupation" },
  { key: "appearance", label: "Appearance", multiline: true },
  { key: "personality", label: "Personality", multiline: true },
  { key: "wants", label: "Wants (external goal)", multiline: true },
  { key: "needs", label: "Needs (internal need)", multiline: true },
  { key: "fear", label: "Fear", multiline: true },
  { key: "flaw", label: "Flaw", multiline: true },
  { key: "strengths", label: "Strengths", multiline: true },
  { key: "backstory", label: "Backstory", multiline: true },
  { key: "voice", label: "Voice / Speech pattern", multiline: true },
  { key: "arc", label: "Arc", multiline: true },
  { key: "relationships", label: "Relationships", multiline: true },
  { key: "notes", label: "Notes", multiline: true },
  { key: "tags", label: "Tags" },
];

const ALL_FIELDS = [
  { key: "name", label: "Name" },
  ...SHALLOW_FIELDS,
  ...DEEP_FIELDS,
];

const emptyCharacter = () => ({
  name: "", aliases: [],
  gender: "", age: "", description: "",
  role: "", occupation: "", appearance: "", personality: "", backstory: "",
  wants: "", needs: "", fear: "", flaw: "", strengths: "", voice: "", arc: "",
  relationships: "", notes: "", tags: "", photos: [], color_tag: "",
});

// Screen-time estimate: the app treats ~190 words as one script page ≈ one minute.
const RATE_WPM = 190;
function clock(words) {
  const secs = Math.round((words / RATE_WPM) * 60);
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function CharactersPanel({ scriptId, refreshKey, elements = [], jumpToElement, onLocateCharacter }) {
  const confirm = useConfirm();
  const openImage = useLightbox();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [expanded, setExpanded] = useState({});
  const [view, setView] = useState({});
  const [mergePicker, setMergePicker] = useState(false);
  const [mergeIds, setMergeIds] = useState([]);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [sortMode, setSortMode] = useState("custom");
  const [usage, setUsage] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractingId, setExtractingId] = useState(null);
  const [review, setReview] = useState(null); // [{charId, name, proposals}]
  const reorderTimer = useRef(null);

  const analysis = useMemo(() => analyzeScriptCharacters(elements), [elements]);

  const charKeys = useCallback((c) => {
    const arr = [c.name, ...((c.aliases) || [])].map((s) => (s || "").toUpperCase().trim()).filter(Boolean);
    return Array.from(new Set(arr));
  }, []);
  const charStat = useCallback((c) => {
    let firstIndex = Infinity, firstScene = 0, dialogueWords = 0, actionWords = 0, occurrences = [];
    for (const k of charKeys(c)) {
      const s = analysis.stats[k];
      if (s) {
        if (s.firstIndex < firstIndex) { firstIndex = s.firstIndex; firstScene = s.firstScene; }
        dialogueWords += s.dialogueWords || 0;
        actionWords += s.actionWords || 0;
        occurrences = occurrences.concat(s.occurrences);
      }
    }
    occurrences.sort((a, b) => a.index - b.index);
    return { firstIndex, firstScene, dialogueWords, actionWords, totalWords: dialogueWords + actionWords, occurrences };
  }, [analysis, charKeys]);

  const load = useCallback(async () => {
    if (!scriptId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/scripts/${scriptId}/characters`);
      setItems(data.characters || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [scriptId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const createCharacter = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await api.post(`/scripts/${scriptId}/characters`, { name });
      setItems((prev) => [...prev, data.character]);
      setExpanded((p) => ({ ...p, [data.character.id]: true }));
      setNewName("");
      setCreating(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add character");
    }
  };

  const doDeleteCharacter = async (id) => {
    try {
      await api.delete(`/scripts/${scriptId}/characters/${id}`);
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch { toast.error("Could not delete"); }
  };

  const removeCharacter = async (id) => {
    const c = items.find((x) => x.id === id);
    const occ = c ? charStat(c).occurrences : [];
    if (occ.length > 0) {
      // Active character — warn and show where it's used before deleting.
      setUsage({ character: c, occurrences: occ });
      return;
    }
    const ok = await confirm({
      title: "Delete this character profile?",
      description: "The character card and all its details will be removed. Your script text isn't affected.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    doDeleteCharacter(id);
  };

  const saveField = async (id, patch) => {
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    try {
      const current = items.find((c) => c.id === id) || {};
      const body = { ...emptyCharacter(), ...current, ...patch };
      delete body.id; delete body.script_id; delete body.created_at; delete body.auto_created;
      await api.put(`/scripts/${scriptId}/characters/${id}`, body);
    } catch { toast.error("Could not save"); load(); }
  };

  // ---- Update profiles from the script (grounded extraction) ----
  const extractAll = async () => {
    if (extracting) return;
    setExtracting(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/characters/extract-all`, { mode: "fill" });
      await load();
      const withProposals = (data.results || []).filter((r) => Object.keys(r.proposals || {}).length > 0);
      if (data.fields_filled > 0) {
        toast.success(`Read the script — filled ${data.fields_filled} empty field${data.fields_filled === 1 ? "" : "s"} across ${data.updated} character${data.updated === 1 ? "" : "s"}.`);
      } else if (withProposals.length === 0) {
        toast("Read the script — the profiles already match what's on the page.");
      }
      if (withProposals.length > 0) {
        setReview(withProposals.map((r) => ({ charId: r.id, name: r.name, proposals: r.proposals })));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read the script right now.");
    } finally { setExtracting(false); }
  };

  const extractOne = async (c) => {
    if (extractingId) return;
    setExtractingId(c.id);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/characters/${c.id}/extract`, { mode: "fill" });
      if (data.character) setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data.character } : x)));
      if (data.note) { toast(data.note); return; }
      const nApplied = Object.keys(data.applied || {}).length;
      const proposals = data.proposals || {};
      if (nApplied > 0) toast.success(`Filled ${nApplied} empty field${nApplied === 1 ? "" : "s"} from the script.`);
      if (Object.keys(proposals).length > 0) {
        setReview([{ charId: c.id, name: c.name, proposals }]);
      } else if (nApplied === 0) {
        toast(`${c.name}'s profile already matches the script.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't read the script right now.");
    } finally { setExtractingId(null); }
  };

  const applyReview = async (selections) => {
    // selections: {charId: {field: newValue}}
    let n = 0;
    for (const [charId, patch] of Object.entries(selections)) {
      const keys = Object.keys(patch);
      if (!keys.length) continue;
      n += keys.length;
      await saveField(charId, patch);
    }
    setReview(null);
    if (n > 0) toast.success(`Updated ${n} field${n === 1 ? "" : "s"} from the script.`);
  };

  const uploadPhoto = async (id, file) => {
    if (!file) return;
    if (file.size > 4_500_000) {
      toast.error("Image must be under ~4MB");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      const { data } = await api.post(`/scripts/${scriptId}/characters/${id}/photos`, { data_url: dataUrl });
      setItems((prev) => prev.map((c) => (c.id === id ? data.character : c)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    }
  };

  const removePhoto = async (id, idx) => {
    const ok = await confirm({
      title: "Delete this image?",
      description: "This photo will be removed from the character profile.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.delete(`/scripts/${scriptId}/characters/${id}/photos/${idx}`);
      setItems((prev) => prev.map((c) => (c.id === id ? data.character : c)));
    } catch { toast.error("Could not remove photo"); }
  };

  // ----- Merge flow ------------------------------------------------------
  const startMerge = () => {
    setMergePicker(true);
    setMergeIds([]);
  };
  const cancelMerge = () => {
    setMergePicker(false);
    setMergeIds([]);
    setMergeModalOpen(false);
  };
  const toggleMergeId = (id) => {
    setMergeIds((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 2) return [cur[1], id]; // rolling pick of 2
      return [...cur, id];
    });
  };
  const openMergeModal = () => {
    if (mergeIds.length !== 2) {
      toast.error("Pick exactly 2 characters to compare.");
      return;
    }
    setMergeModalOpen(true);
  };
  const handleMergeSuccess = () => {
    cancelMerge();
    load();
  };

  const persistOrder = (next) => {
    setItems(next);
    if (reorderTimer.current) clearTimeout(reorderTimer.current);
    reorderTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put(`/scripts/${scriptId}/characters/reorder`, {
          items: next.map((c) => ({ id: c.id, pinned: !!c.pinned })),
        });
        if (data.characters) setItems(data.characters);
      } catch { toast.error("Could not save the new order"); }
    }, 600);
  };

  const pinned = items.filter((c) => c.pinned);
  const unpinned = items.filter((c) => !c.pinned);

  const togglePin = (id) => {
    const updated = items.map((c) => (c.id === id ? { ...c, pinned: !c.pinned } : c));
    const next = [...updated.filter((c) => c.pinned), ...updated.filter((c) => !c.pinned)];
    persistOrder(next);
  };
  const onReorderPinned = (newP) => persistOrder([...newP, ...unpinned]);
  const onReorderUnpinned = (newU) => persistOrder([...pinned, ...newU]);

  const sortedItems = useMemo(() => {
    if (sortMode === "custom") return items;
    const copy = [...items];
    if (sortMode === "name") {
      copy.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortMode === "appearance") {
      copy.sort((a, b) => (charStat(a).firstIndex - charStat(b).firstIndex) || (a.name || "").localeCompare(b.name || ""));
    } else if (sortMode === "screentime") {
      copy.sort((a, b) => (charStat(b).totalWords - charStat(a).totalWords) || (a.name || "").localeCompare(b.name || ""));
    }
    return copy;
  }, [items, sortMode, charStat]);

  const metaFor = (c) => {
    if (sortMode === "appearance") {
      const st = charStat(c);
      return st.firstIndex === Infinity ? "Not in script" : `First in scene ${st.firstScene}`;
    }
    if (sortMode === "screentime") {
      const st = charStat(c);
      if (st.totalWords <= 0) return "No screen time yet";
      return `≈ ${clock(st.totalWords)} total · ${clock(st.dialogueWords)} dialogue / ${clock(st.actionWords)} action`;
    }
    return null;
  };

  const renderCard = (c, draggable) => (
    <CharacterCard
      key={c.id}
      character={c}
      draggable={draggable}
      meta={metaFor(c)}
      isExpanded={!!expanded[c.id]}
      viewMode={view[c.id] || "shallow"}
      pickingMerge={mergePicker}
      picked={mergeIds.includes(c.id)}
      onTogglePick={() => toggleMergeId(c.id)}
      onTogglePin={() => togglePin(c.id)}
      onToggleExpand={() => setExpanded((p) => ({ ...p, [c.id]: !p[c.id] }))}
      onSetView={(v) => setView((p) => ({ ...p, [c.id]: v }))}
      onSaveField={(patch) => saveField(c.id, patch)}
      onLocate={() => {
        if (!onLocateCharacter) return;
        const st = charStat(c);
        if (!st.occurrences.length) { toast(`${c.name || "This character"} isn't in the script yet.`); return; }
        onLocateCharacter({ name: c.name, occurrences: st.occurrences });
      }}
      onRemove={() => removeCharacter(c.id)}
      onUploadPhoto={(file) => uploadPhoto(c.id, file)}
      onRemovePhoto={(idx) => removePhoto(c.id, idx)}
      onOpenImage={openImage}
      onExtract={() => extractOne(c)}
      extractBusy={extractingId === c.id}
    />
  );

  return (
    <div className="p-3" data-testid="characters-panel">
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setCreating((o) => !o)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-wider rounded border border-dashed border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"
          data-testid="add-character-btn"
        >
          <Plus size={14} strokeWidth={1.5} /> New
        </button>
        {items.length > 0 && !mergePicker && (
          <button
            onClick={extractAll}
            disabled={extracting}
            className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider rounded border border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)] disabled:opacity-60"
            data-testid="characters-extract-all-btn"
            title="Re-read the script and update every profile with what's on the page — empty fields fill in, changes to filled fields come back for your review"
          >
            {extracting ? <Loader2 size={14} className="animate-spin" /> : <BookOpenCheck size={14} strokeWidth={1.5} />}
            {extracting ? "Reading…" : "Update all"}
          </button>
        )}
        <button
          onClick={mergePicker ? cancelMerge : startMerge}
          className={`flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider rounded ${mergePicker ? "bg-[var(--brand)] text-white" : "border border-[var(--line-strong)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"}`}
          data-testid="merge-character-btn"
        >
          <GitMerge size={14} strokeWidth={1.5} />
          {mergePicker ? "Cancel" : "Merge"}
        </button>
      </div>

      {!mergePicker && items.length > 1 && (
        <div className="flex items-center gap-2 mb-3" data-testid="character-sort-row">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] shrink-0">Sort</span>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            data-testid="character-sort-select"
            className="text-xs bg-white border border-[var(--line)] rounded px-2 py-1 outline-none focus:border-[var(--brand)] text-[var(--ink-secondary)] flex-1"
          >
            <option value="custom">Custom order (drag)</option>
            <option value="name">Name (A–Z)</option>
            <option value="appearance">Appearance</option>
            <option value="screentime">Screen time</option>
          </select>
        </div>
      )}

      {creating && (
        <form onSubmit={createCharacter} className="bg-white border border-[var(--line)] rounded p-3 mb-3 space-y-2" data-testid="character-create-form">
          <input
            autoFocus
            placeholder="Character name (e.g. CHRIS)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="character-name-input"
            className="w-full text-sm bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-1.5 uppercase tracking-wide"
          />
          <div className="flex gap-2 pt-1">
            <button type="submit" className="btn-brand text-xs px-3 py-1.5" data-testid="character-save-btn">Save</button>
            <button type="button" onClick={() => { setCreating(false); setNewName(""); }} className="btn-ghost text-xs px-3 py-1.5" data-testid="character-cancel-btn">Cancel</button>
          </div>
        </form>
      )}

      {mergePicker && (
        <div className="bg-white border border-[var(--brand)]/30 rounded p-3 mb-3 space-y-3" data-testid="merge-picker">
          <div className="flex items-start gap-2">
            <AlertCircle size={16} strokeWidth={1.5} className="text-[var(--brand)] mt-0.5 shrink-0" />
            <div>
              <h4 className="text-sm font-semibold mb-1">Pick two characters to compare</h4>
              <p className="text-xs text-[var(--ink-secondary)] leading-relaxed">
                Tap two cards below, then choose which one stays and pick field-by-field what to keep.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-[var(--ink-muted)]">{mergeIds.length}/2 selected</p>
            <button
              onClick={openMergeModal}
              disabled={mergeIds.length !== 2}
              className="btn-brand text-xs px-3 py-1.5 disabled:opacity-50"
              data-testid="merge-compare-btn"
            >
              Compare →
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-[var(--ink-muted)] italic px-4 py-6 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-[var(--ink-muted)] italic px-4 py-6 text-center" data-testid="characters-empty">
          No characters yet. Names typed in CHARACTER lines will appear here automatically.
        </p>
      ) : mergePicker ? (
        <div className="space-y-2" data-testid="characters-list">
          {items.map((c) => renderCard(c, false))}
        </div>
      ) : sortMode !== "custom" ? (
        <div className="space-y-2" data-testid="characters-list">
          {sortedItems.map((c) => renderCard(c, false))}
        </div>
      ) : (
        <div data-testid="characters-list">
          {pinned.length > 0 && (
            <div className="mb-3" data-testid="characters-pinned-section">
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5 px-0.5">
                <Pin size={11} className="fill-current" /> Pinned
              </div>
              <Reorder.Group axis="y" values={pinned} onReorder={onReorderPinned} className="space-y-2 list-none m-0 p-0" as="div">
                {pinned.map((c) => renderCard(c, true))}
              </Reorder.Group>
            </div>
          )}
          <Reorder.Group axis="y" values={unpinned} onReorder={onReorderUnpinned} className="space-y-2 list-none m-0 p-0" as="div">
            {unpinned.map((c) => renderCard(c, true))}
          </Reorder.Group>
        </div>
      )}

      {mergeModalOpen && mergeIds.length === 2 && (
        <MergeModal
          scriptId={scriptId}
          a={items.find((c) => c.id === mergeIds[0])}
          b={items.find((c) => c.id === mergeIds[1])}
          onClose={() => setMergeModalOpen(false)}
          onSuccess={handleMergeSuccess}
        />
      )}

      {usage && (
        <CharacterUsageModal
          usage={usage}
          onCancel={() => setUsage(null)}
          onJump={(elementId) => { setUsage(null); if (jumpToElement) jumpToElement(elementId); }}
          onDelete={() => { const id = usage.character.id; setUsage(null); doDeleteCharacter(id); }}
        />
      )}

      {review && review.length > 0 && (
        <ExtractReviewModal
          entries={review}
          onApply={applyReview}
          onClose={() => setReview(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function CharacterCard({
  character, draggable, isExpanded, viewMode, pickingMerge, picked, onTogglePick, meta,
  onToggleExpand, onSetView, onSaveField, onLocate, onRemove, onUploadPhoto, onRemovePhoto, onOpenImage, onTogglePin,
  onExtract, extractBusy,
}) {
  const fileInputRef = useRef(null);
  const controls = useDragControls();
  // A single physical click on a photo can surface a duplicate click event
  // (same timeStamp) on both the <img> and its overlaid delete button. Claim the
  // event by timeStamp so only the first (real target) handler runs.
  const clickClaimRef = useRef(-1);
  const claimClick = (e) => {
    if (e.timeStamp === clickClaimRef.current) return false;
    clickClaimRef.current = e.timeStamp;
    return true;
  };
  const [hideEmpty, setHideEmpty] = useState(false);
  const aliasesText = (character.aliases || []).join(", ");
  const [aliasesDraft, setAliasesDraft] = useState(aliasesText);
  useEffect(() => { setAliasesDraft((character.aliases || []).join(", ")); }, [character.id, character.aliases]);

  const commitAliases = () => {
    const arr = aliasesDraft.split(",").map((s) => s.trim()).filter(Boolean);
    if (JSON.stringify(arr) !== JSON.stringify(character.aliases || [])) {
      onSaveField({ aliases: arr });
    }
  };

  const handleHeaderClick = () => {
    if (pickingMerge) onTogglePick();
    else onToggleExpand();
  };

  const cardClass = `bg-white border rounded p-3 transition-colors ${picked ? "border-[var(--brand)] ring-1 ring-[var(--brand)]" : "border-[var(--line)]"} ${pickingMerge ? "cursor-pointer" : ""}`;

  const header = (
      <header className="flex items-start gap-2">
        {draggable && (
          <button
            onPointerDown={(e) => controls.start(e)}
            className="mt-1 text-[var(--ink-muted)] hover:text-[var(--ink-primary)] cursor-grab active:cursor-grabbing touch-none shrink-0"
            data-testid={`character-drag-${character.id}`}
            title="Drag to reorder"
          ><GripVertical size={14} /></button>
        )}
        {pickingMerge ? (
          <span className={`mt-1 w-4 h-4 rounded border-2 flex items-center justify-center ${picked ? "bg-[var(--brand)] border-[var(--brand)]" : "border-[var(--line-strong)]"}`}>
            {picked && <Check size={10} strokeWidth={3} className="text-white" />}
          </span>
        ) : (
          <button onClick={onToggleExpand} className="text-[var(--ink-muted)] hover:text-[var(--ink-primary)] mt-1" data-testid={`character-toggle-${character.id}`}>
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        )}
        <Avatar character={character} />
        <div className="flex-1 min-w-0">
          {pickingMerge ? (
            <div className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-primary)]">{character.name}</div>
          ) : (
            <input
              value={character.name}
              onChange={(e) => onSaveField({ name: e.target.value })}
              onFocus={onLocate}
              title="Click to find them in the script — every scene they appear in lights up"
              data-testid={`character-name-${character.id}`}
              className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-primary)] bg-transparent border-b border-transparent focus:border-[var(--brand)] outline-none w-full py-0.5 cursor-pointer focus:cursor-text"
            />
          )}
          {character.auto_created && !isExpanded && !pickingMerge && (
            <p className="text-[10px] text-[var(--ink-muted)] italic mt-0.5">Auto-added from script</p>
          )}
          {character.aliases?.length > 0 && !isExpanded && (
            <p className="text-[11px] text-[var(--ink-muted)] mt-0.5 truncate">aka {character.aliases.join(", ")}</p>
          )}
          {meta && !isExpanded && !pickingMerge && (
            <p className="text-[10px] text-[var(--ink-muted)] mt-0.5" data-testid={`character-meta-${character.id}`}>{meta}</p>
          )}
        </div>
        {draggable && (
          <button
            onClick={onTogglePin}
            className={`p-1 shrink-0 hover:text-[var(--brand)] ${character.pinned ? "text-[var(--brand)]" : "text-[var(--ink-muted)]"}`}
            data-testid={`character-pin-${character.id}`}
            title={character.pinned ? "Unpin" : "Pin to top"}
          >
            <Pin size={13} className={character.pinned ? "fill-current" : ""} />
          </button>
        )}
        {!pickingMerge && (
          <button onClick={onRemove} className="text-[var(--ink-muted)] hover:text-[var(--err)] p-1" data-testid={`character-delete-${character.id}`}>
            <Trash2 size={13} />
          </button>
        )}
      </header>
  );

  const detail = isExpanded && !pickingMerge && (
        <div className="mt-3 pt-3 border-t border-[var(--line)] space-y-3" data-testid={`character-detail-${character.id}`}>
          {onExtract && (
            <button
              onClick={onExtract}
              disabled={extractBusy}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-wider rounded border border-dashed border-[var(--brand)]/40 text-[var(--brand)] hover:bg-[var(--brand-soft)] disabled:opacity-60"
              data-testid={`character-extract-${character.id}`}
              title="Read the current draft and update this profile with what's on the page — empty fields fill in, changes to filled fields come back for your review"
            >
              {extractBusy ? <Loader2 size={12} className="animate-spin" /> : <BookOpenCheck size={12} strokeWidth={1.5} />}
              {extractBusy ? "Reading the script…" : "Update from script"}
            </button>
          )}
          <div className="flex items-center gap-1 bg-[var(--canvas)] rounded p-0.5 text-[10px] uppercase tracking-wider">
            <button
              onClick={() => onSetView("shallow")}
              className={`flex-1 py-1.5 rounded ${viewMode === "shallow" ? "bg-white text-[var(--ink-primary)] shadow-sm" : "text-[var(--ink-muted)]"}`}
              data-testid={`character-view-shallow-${character.id}`}
            >Shallow</button>
            <button
              onClick={() => onSetView("deep")}
              className={`flex-1 py-1.5 rounded ${viewMode === "deep" ? "bg-white text-[var(--ink-primary)] shadow-sm" : "text-[var(--ink-muted)]"}`}
              data-testid={`character-view-deep-${character.id}`}
            >Deep</button>
          </div>

          <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--ink-muted)] cursor-pointer select-none" data-testid={`character-hide-empty-label-${character.id}`}>
            <input
              type="checkbox"
              checked={hideEmpty}
              onChange={(e) => setHideEmpty(e.target.checked)}
              className="accent-[var(--brand)]"
              data-testid={`character-hide-empty-${character.id}`}
            />
            Hide empty fields
          </label>

          {(!hideEmpty || (character.aliases?.length > 0)) && (
            <Field label="Aliases">
              <input
                value={aliasesDraft}
                onChange={(e) => setAliasesDraft(e.target.value)}
                onBlur={commitAliases}
                data-testid={`character-aliases-${character.id}`}
                className="w-full text-sm bg-[var(--canvas)] border border-[var(--line)] rounded focus:border-[var(--brand)] outline-none px-2 py-1.5"
              />
            </Field>
          )}

          <Field label={`Photos${character.photos?.length ? ` (${character.photos.length})` : ""}`}>
            <div className="flex flex-wrap gap-2">
              {(character.photos || []).map((url, i) => (
                <div key={i} className="relative group">
                  <img src={url} alt={`${character.name} ${i + 1}`} onClick={(e) => { if (claimClick(e)) onOpenImage(url); }} className="w-14 h-14 object-cover rounded border border-[var(--line)] cursor-zoom-in" data-testid={`character-photo-${character.id}-${i}`} />
                  <button
                    onClick={(e) => { e.stopPropagation(); if (claimClick(e)) onRemovePhoto(i); }}
                    className="absolute -top-1 -right-1 bg-white border border-[var(--line)] rounded-full p-0.5 opacity-0 group-hover:opacity-100"
                    data-testid={`character-photo-remove-${character.id}-${i}`}
                  ><X size={10} strokeWidth={2} className="text-[var(--err)]" /></button>
                </div>
              ))}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-14 h-14 rounded border border-dashed border-[var(--line-strong)] flex items-center justify-center text-[var(--ink-muted)] hover:bg-[var(--warm-accent)]"
                data-testid={`character-photo-upload-${character.id}`}
              ><Upload size={14} strokeWidth={1.5} /></button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach((f) => onUploadPhoto(f));
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                data-testid={`character-photo-input-${character.id}`}
              />
            </div>
          </Field>

          {(hideEmpty ? SHALLOW_FIELDS.filter((f) => (character[f.key] || "").toString().trim()) : SHALLOW_FIELDS).map((f) => (
            <Field key={f.key} label={f.label}>
              <FieldInput value={character[f.key] || ""} onCommit={(v) => onSaveField({ [f.key]: v })} multiline={f.multiline} testId={`character-${f.key}-${character.id}`} />
            </Field>
          ))}
          {viewMode === "deep" && (hideEmpty ? DEEP_FIELDS.filter((f) => (character[f.key] || "").toString().trim()) : DEEP_FIELDS).map((f) => (
            <Field key={f.key} label={f.label}>
              <FieldInput value={character[f.key] || ""} onCommit={(v) => onSaveField({ [f.key]: v })} multiline={f.multiline} testId={`character-${f.key}-${character.id}`} />
            </Field>
          ))}
        </div>
  );

  if (!draggable) {
    return (
      <article
        className={cardClass}
        data-testid={`character-card-${character.id}`}
        onClick={pickingMerge ? handleHeaderClick : undefined}
      >
        {header}
        {detail}
      </article>
    );
  }

  return (
    <Reorder.Item
      value={character}
      dragListener={false}
      dragControls={controls}
      className={`${cardClass} list-none`}
      data-testid={`character-card-${character.id}`}
    >
      {header}
      {detail}
    </Reorder.Item>
  );
}

function Avatar({ character }) {
  const first = (character.photos || [])[0];
  if (first) return <img src={first} alt={character.name} className="w-10 h-10 object-cover rounded-full border border-[var(--line)]" />;
  return (
    <div className="w-10 h-10 rounded-full bg-[var(--warm-accent)] flex items-center justify-center text-[var(--ink-secondary)] text-xs font-semibold uppercase">
      {(character.name || "?").slice(0, 2)}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1">{label}</span>
      {children}
    </label>
  );
}

function FieldInput({ value, onCommit, multiline, testId }) {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const handleBlur = () => { if ((draft || "") !== (value || "")) onCommit(draft || ""); };

  if (multiline) {
    return (
      <textarea
        value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={handleBlur}
        data-testid={testId}
        className="w-full text-sm bg-[var(--canvas)] border border-[var(--line)] rounded focus:border-[var(--brand)] outline-none px-2 py-1.5 min-h-[48px] resize-none leading-relaxed"
      />
    );
  }
  return (
    <input
      value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={handleBlur}
      data-testid={testId}
      className="w-full text-sm bg-[var(--canvas)] border border-[var(--line)] rounded focus:border-[var(--brand)] outline-none px-2 py-1.5"
    />
  );
}

// ---------------------------------------------------------------------------
// Merge Modal — side-by-side comparison
// ---------------------------------------------------------------------------
function MergeModal({ scriptId, a, b, onClose, onSuccess }) {
  // mainId = which character is "main" (target). The other is the source.
  const [mainId, setMainId] = useState(a.id);
  // For each field: 'a' | 'b' | 'combine' | string (custom value)
  const [picks, setPicks] = useState(() => {
    const initial = {};
    for (const f of ALL_FIELDS) {
      initial[f.key] = "a"; // default to A's value
    }
    return initial;
  });
  // Photos: which photo indices (by combined index) to keep
  const allPhotos = useMemo(() => {
    return [
      ...(a.photos || []).map((url, i) => ({ url, from: "a", origIdx: i })),
      ...(b.photos || []).map((url, i) => ({ url, from: "b", origIdx: i })),
    ];
  }, [a, b]);
  const [keepPhotos, setKeepPhotos] = useState(() => allPhotos.map(() => true));

  const main = mainId === a.id ? a : b;
  const source = mainId === a.id ? b : a;

  // Build the final merged record from picks
  const merged = useMemo(() => {
    const out = {};
    for (const f of ALL_FIELDS) {
      const pick = picks[f.key];
      const va = (a[f.key] || "").toString();
      const vb = (b[f.key] || "").toString();
      if (pick === "a") out[f.key] = va;
      else if (pick === "b") out[f.key] = vb;
      else if (pick === "combine") {
        if (!va) out[f.key] = vb;
        else if (!vb) out[f.key] = va;
        else if (f.multiline) out[f.key] = `${va}\n\n${vb}`;
        else out[f.key] = `${va}, ${vb}`;
      } else {
        out[f.key] = pick; // custom string
      }
    }
    return out;
  }, [picks, a, b]);

  const aliases = useMemo(() => {
    const set = new Set();
    const mainName = (merged.name || main.name || "").toUpperCase();
    // Original names from both
    for (const c of [a, b]) {
      if (c.name && c.name.toUpperCase() !== mainName) set.add(c.name);
      for (const al of (c.aliases || [])) {
        if (al.toUpperCase() !== mainName) set.add(al);
      }
    }
    return Array.from(set).sort();
  }, [a, b, merged.name, main.name]);

  const photosToKeep = useMemo(
    () => allPhotos.filter((_, i) => keepPhotos[i]).map((p) => p.url),
    [allPhotos, keepPhotos]
  );

  const submit = async () => {
    try {
      const targetId = mainId;
      const sourceId = mainId === a.id ? b.id : a.id;
      await api.post(`/scripts/${scriptId}/characters/merge`, {
        target_id: targetId,
        source_ids: [sourceId],
        merged_fields: merged,
        merged_aliases: aliases,
        merged_photos: photosToKeep,
      });
      toast.success("Merged");
      onSuccess();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Merge failed");
    }
  };

  // Are A and B values different for a field?
  const differs = (key) => (a[key] || "") !== (b[key] || "");
  const differingFields = ALL_FIELDS.filter((f) => differs(f.key));
  const sameFields = ALL_FIELDS.filter((f) => !differs(f.key) && (a[f.key] || ""));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" data-testid="merge-modal">
      <div className="bg-[var(--canvas)] border border-[var(--line)] rounded-md shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <header className="flex items-center justify-between px-6 py-4 border-b border-[var(--line)]">
          <div>
            <h2 className="font-display text-2xl text-[var(--ink-primary)]">Merge characters</h2>
            <p className="text-xs text-[var(--ink-muted)] mt-0.5">Compare side-by-side, choose what stays. The &ldquo;Main&rdquo; card is what remains; the other will be deleted (its name becomes an alias).</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--ink-primary)] p-2" data-testid="merge-modal-close">
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Pick Main */}
          <section>
            <h3 className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-2">Main card</h3>
            <div className="grid grid-cols-2 gap-3">
              {[a, b].map((c) => {
                const selected = mainId === c.id;
                return (
                  <button
                    key={c.id}
                    onClick={() => setMainId(c.id)}
                    className={`flex items-center gap-3 p-3 rounded border text-left transition-colors ${selected ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--line)] bg-white hover:border-[var(--line-strong)]"}`}
                    data-testid={`merge-main-${c.id}`}
                  >
                    <Avatar character={c} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold uppercase truncate">{c.name}</div>
                      <div className="text-[11px] text-[var(--ink-muted)]">{selected ? "Stays · sources fold in" : "Will be folded in"}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Photos */}
          {allPhotos.length > 0 && (
            <section>
              <h3 className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-2">Photos · keep / drop</h3>
              <div className="flex flex-wrap gap-2">
                {allPhotos.map((p, i) => (
                  <label key={i} className="relative cursor-pointer" data-testid={`merge-photo-${i}`}>
                    <input
                      type="checkbox"
                      checked={keepPhotos[i]}
                      onChange={(e) => {
                        const next = [...keepPhotos];
                        next[i] = e.target.checked;
                        setKeepPhotos(next);
                      }}
                      className="absolute top-1 left-1 z-10 accent-[var(--brand)]"
                    />
                    <img src={p.url} alt="" className={`w-16 h-16 object-cover rounded border ${keepPhotos[i] ? "border-[var(--brand)]" : "border-[var(--line)] opacity-40"}`} />
                    <span className="absolute bottom-0 right-0 text-[9px] uppercase bg-white/90 px-1 rounded-tl">{p.from === "a" ? a.name?.slice(0, 6) : b.name?.slice(0, 6)}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          {/* Differing fields */}
          <section>
            <h3 className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-3">Fields that differ — pick which to keep</h3>
            {differingFields.length === 0 ? (
              <p className="text-sm text-[var(--ink-muted)] italic">All filled fields match. Just confirm to merge.</p>
            ) : (
              <div className="space-y-4">
                {differingFields.map((f) => (
                  <FieldComparison
                    key={f.key}
                    field={f}
                    valueA={a[f.key] || ""}
                    valueB={b[f.key] || ""}
                    nameA={a.name}
                    nameB={b.name}
                    pick={picks[f.key]}
                    onPick={(p) => setPicks({ ...picks, [f.key]: p })}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Aliases preview */}
          <section>
            <h3 className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-2">Aliases (auto-merged)</h3>
            <p className="text-sm text-[var(--ink-secondary)]" data-testid="merge-aliases-preview">
              {aliases.length === 0 ? <em>None</em> : aliases.join(" · ")}
            </p>
          </section>

          {/* Identical fields collapsed for reference */}
          {sameFields.length > 0 && (
            <details className="border-t border-[var(--line)] pt-4">
              <summary className="cursor-pointer text-[11px] uppercase tracking-widest text-[var(--ink-muted)]">
                Fields that already match ({sameFields.length})
              </summary>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                {sameFields.map((f) => (
                  <React.Fragment key={f.key}>
                    <dt className="text-[var(--ink-muted)]">{f.label}</dt>
                    <dd className="text-[var(--ink-primary)] truncate">{a[f.key]}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </details>
          )}
        </div>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--line)]">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="merge-modal-cancel">Cancel</button>
          <button onClick={submit} className="btn-brand text-sm" data-testid="merge-modal-apply">Apply merge</button>
        </footer>
      </div>
    </div>
  );
}

function FieldComparison({ field, valueA, valueB, nameA, nameB, pick, onPick }) {
  return (
    <div className="border border-[var(--line)] rounded bg-white p-3" data-testid={`merge-field-${field.key}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)]">{field.label}</span>
        {field.multiline && (
          <button
            onClick={() => onPick("combine")}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${pick === "combine" ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "border-[var(--line)] text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]"}`}
            data-testid={`merge-combine-${field.key}`}
          >
            Combine both
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FieldOption
          name={nameA} value={valueA} selected={pick === "a"}
          onClick={() => onPick("a")} testId={`merge-${field.key}-a`}
        />
        <FieldOption
          name={nameB} value={valueB} selected={pick === "b"}
          onClick={() => onPick("b")} testId={`merge-${field.key}-b`}
        />
      </div>
    </div>
  );
}

function FieldOption({ name, value, selected, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left p-2.5 rounded border min-h-[60px] transition-colors ${selected ? "border-[var(--brand)] bg-[var(--brand-soft)]" : "border-[var(--line)] hover:border-[var(--line-strong)] bg-[var(--canvas)]"}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1">{name}</div>
      <div className="text-sm text-[var(--ink-primary)] whitespace-pre-wrap break-words">
        {value || <em className="text-[var(--ink-muted)]">empty</em>}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Character usage modal — shown when deleting a character still in the script
// ---------------------------------------------------------------------------
function CharacterUsageModal({ usage, onCancel, onJump, onDelete }) {
  const { character, occurrences } = usage;
  const count = occurrences.length;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="character-usage-modal">
      <div className="bg-[var(--paper)] border border-[var(--line)] rounded-md shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="h-1 w-full" style={{ background: "var(--warn)" }} />
        <header className="px-6 pt-5 pb-3">
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(212,163,115,0.18)", color: "var(--warn)" }}>
              <AlertCircle size={18} strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-xl text-[var(--ink-primary)] leading-snug">
                Delete {character?.name}?
              </h2>
              <p className="text-sm text-[var(--ink-secondary)] mt-1 leading-relaxed">
                {character?.name} still appears <strong>{count}</strong> time{count === 1 ? "" : "s"} in your script.
                Deleting this profile won't change your script text — those lines stay exactly as written.
              </p>
            </div>
          </div>
        </header>

        <div className="px-6 overflow-y-auto flex-1">
          <p className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-2">Where it's used — click to jump</p>
          <ul className="space-y-1.5 list-none m-0 p-0 mb-2">
            {occurrences.map((o, i) => (
              <li key={`${o.elementId}-${i}`}>
                <button
                  onClick={() => onJump(o.elementId)}
                  className="w-full text-left flex items-start gap-2 px-3 py-2 rounded border border-[var(--line)] bg-[var(--canvas)] hover:border-[var(--brand)] hover:bg-[var(--brand-soft)] transition-colors"
                  data-testid={`character-usage-link-${i}`}
                >
                  <MapPin size={13} className="text-[var(--brand)] mt-0.5 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-[var(--ink-primary)]">Scene {o.sceneNumber || "—"}</span>
                    <span className="block text-[11px] text-[var(--ink-muted)] uppercase tracking-wide truncate">{o.sceneHeading || "(untitled scene)"}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <footer className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-[var(--line)]">
          <button onClick={onCancel} className="px-4 py-2 text-sm rounded-md text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]" data-testid="character-usage-cancel">Keep character</button>
          <button onClick={onDelete} className="px-4 py-2 text-sm rounded-md text-white font-medium" style={{ background: "var(--err)" }} data-testid="character-usage-delete">Delete anyway</button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extract review — the script's read disagrees with fields the writer already
// filled in. Nothing is overwritten without approval.
// ---------------------------------------------------------------------------
const FIELD_LABELS = ALL_FIELDS.reduce((m, f) => { m[f.key] = f.label; return m; }, {});

function ExtractReviewModal({ entries, onApply, onClose }) {
  // checked: {"charId:field": bool} — everything selected by default so one
  // click accepts the whole read ("overwrite all"); uncheck to keep your own.
  const [checked, setChecked] = useState(() => {
    const init = {};
    for (const e of entries) for (const f of Object.keys(e.proposals || {})) init[`${e.charId}:${f}`] = true;
    return init;
  });
  const [applying, setApplying] = useState(false);

  const total = entries.reduce((n, e) => n + Object.keys(e.proposals || {}).length, 0);
  const selectedCount = Object.values(checked).filter(Boolean).length;
  const allChecked = selectedCount === total;

  const toggleAll = () => {
    const next = {};
    for (const e of entries) for (const f of Object.keys(e.proposals || {})) next[`${e.charId}:${f}`] = !allChecked;
    setChecked(next);
  };

  const apply = async () => {
    setApplying(true);
    const selections = {};
    for (const e of entries) {
      const patch = {};
      for (const [f, p] of Object.entries(e.proposals || {})) {
        if (checked[`${e.charId}:${f}`]) patch[f] = p.proposed;
      }
      selections[e.charId] = patch;
    }
    try { await onApply(selections); } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6" data-testid="extract-review-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl max-h-[85vh] bg-[var(--paper)] rounded-xl shadow-2xl border border-[var(--line)] flex flex-col overflow-hidden">
        <header className="px-6 py-4 border-b border-[var(--line)] shrink-0 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg text-[var(--ink-primary)] flex items-center gap-2">
              <BookOpenCheck size={17} className="text-[var(--brand)]" /> The script reads differently
            </h2>
            <p className="text-xs text-[var(--ink-muted)] mt-1 leading-relaxed">
              These fields already have your text, but the current draft supports something different.
              Approve the changes you want — unchecked fields keep what you wrote.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)] shrink-0" data-testid="extract-review-close"><X size={16} /></button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-5">
          {entries.map((e) => (
            <div key={e.charId} data-testid={`extract-review-char-${e.charId}`}>
              <div className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-primary)] mb-2">{e.name}</div>
              <div className="space-y-2.5">
                {Object.entries(e.proposals || {}).map(([f, p]) => {
                  const key = `${e.charId}:${f}`;
                  return (
                    <label key={f} className={`flex gap-2.5 rounded-lg border p-2.5 cursor-pointer transition-colors ${checked[key] ? "border-[var(--brand)]/40 bg-[var(--brand-soft)]/40" : "border-[var(--line)]"}`}>
                      <input
                        type="checkbox"
                        checked={!!checked[key]}
                        onChange={() => setChecked((prev) => ({ ...prev, [key]: !prev[key] }))}
                        className="accent-[var(--brand)] mt-0.5 shrink-0"
                        data-testid={`extract-review-check-${e.charId}-${f}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mb-1">{FIELD_LABELS[f] || f}</span>
                        <span className="block text-[12px] text-[var(--ink-muted)] line-through decoration-[var(--line-strong)] leading-relaxed">{p.current}</span>
                        <span className="block text-[13px] text-[var(--ink-primary)] mt-1 leading-relaxed">{p.proposed}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <footer className="flex items-center justify-between gap-2.5 px-6 py-3.5 border-t border-[var(--line)] shrink-0">
          <button onClick={toggleAll} className="text-xs text-[var(--ink-secondary)] hover:text-[var(--ink-primary)] underline-offset-2 hover:underline" data-testid="extract-review-selectall">
            {allChecked ? "Deselect all" : "Select all (overwrite everything)"}
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3.5 py-2 text-sm rounded-md text-[var(--ink-secondary)] hover:bg-[var(--warm-accent)]" data-testid="extract-review-cancel">Keep mine</button>
            <button
              onClick={apply}
              disabled={applying || selectedCount === 0}
              className="btn-brand text-sm px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="extract-review-apply"
            >
              {applying && <Loader2 size={13} className="animate-spin" />} Apply {selectedCount === total ? "all" : `${selectedCount}`} change{selectedCount === 1 ? "" : "s"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
